/**
 * BwC AI Assistant Panel
 * A chat interface panel that allows users to ask questions about BwC customer data
 * Uses OpenAI to analyze and summarize the data based on user prompts
 */

import {
    Icon,
    Panel,
    PanelType,
    Spinner,
    SpinnerSize,
    TextField,
    IconButton,
    TooltipHost,
    DirectionalHint
} from "@fluentui/react";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import {
    IBwCAIAssistantPanelProps,
    IBwCAIChatMessage,
    IBwCAIPromptScenario,
    IBwCCustomer
} from "./BuildWithCustomer.interfaces";
import { useAskBwCAIAssistantMutation } from "../../slices/BuildWithCustomer.slice";

// Predefined AI prompt scenarios
const AI_PROMPT_SCENARIOS: IBwCAIPromptScenario[] = [
    {
        id: "overall-bwc-insights",
        icon: "Lightbulb",
        title: "Overall BwC Insights",
        description: "Executive-ready summary of feature testing across all BwC engagements",
        prompt: `Build With Customer (BwC) Feature Insights

Objective:
Create an executive-ready summary of feature testing currently in progress across Build With Customer (BwC) engagements, using an Eval AI / startup-style assessment.

Instructions:
Using notes, customer feedback, and engagement summaries, produce the following:

Top-Level Summary (at the top)
- Total number of customers currently testing
- Total number of features under testing
- High-level signal:
  - How many features are going well
  - How many features are facing friction
  - Any features at risk / needing follow-up

Detailed Breakdown by BwC
For each Build With Customer (BwC), summarize in the format below:
- Feature Name:
- Customer(s):
- For each feature under this BwC:
  - Testing Status:  Going well |  Some friction |  Significant friction / blocked
  - What's Working Well: Key positive outcomes or validation from customers
  - Friction / Challenges: Gaps, usability issues, missing capabilities, performance concerns, or integration pain points
  - Key Customer Feedback (themes): Direct signals or summarized sentiment from notes
  - Current Outcome / Signal: Clear recommendation such as: Proceed / scale, Iterate and re-test, Needs product decision, Pause or deprioritize

Cross-Cutting Insights (Optional but Recommended)
- Common patterns across customers
- Repeated friction points across multiple features
- Any early indicators of product-market fit
- Dependencies or asks for engineering, UX, or GTM

Tone & Style Guidelines:
- Concise, startup-style evaluation
- Outcome-oriented, not descriptive
- Use bullets, not paragraphs
- Call out signals and decisions, not raw notes

Output Format:
- Start with Executive Summary
- Follow with BwC-by-BwC breakdown
- End with Key Takeaways / Next Steps`
    },
    {
        id: "weekly-bwc-insights",
        icon: "Calendar",
        title: "This week's BwC Insights",
        description: "Weekly summary focusing on recent activity and changes",
        prompt: `This Week's BwC Activity Summary

Objective:
Create a focused summary of BwC activity from the PAST 7 DAYS ONLY, highlighting what changed this week.

Instructions:
Focus ONLY on recent activity by analyzing:
1. Look at 'lastModifiedNotesDate' - only include customers with notes updated in the past 7 days
2. Look at 'createdOn' - identify any NEW customers added this week
3. Reference the 'Customers Added in the Past Week' data section

Report Structure:

## New Customers This Week
- List all customers added in the past week (from the 'recentCustomers' data)
- For each: Customer Name, Program/Squad, Status, CxE Lead, Region

## Recent Notes Activity (Past 7 Days)
- Which customers had notes updated recently?
- What are the key themes from recent notes?
- Any blockers or issues mentioned in recent notes?

## Weekly Status Changes
- Any customers who changed status this week?
- New "In Progress" customers that started testing?

## Key Highlights
- What's the most important signal from this week's data?
- Any urgent follow-ups needed?

Tone:
- Brief and action-oriented
- Focus on CHANGES and NEW information, not the overall state
- Highlight what's different from last week`
    },
    {
        id: "engagements-hygiene",
        icon: "Health",
        title: "Engagements Hygiene",
        description: "Identify in-progress engagements with missing notes grouped by Feature",
        prompt: `Engagements Hygiene Report

Objective:
Identify in-progress customer engagements where notes are missing, grouped by Feature Name, to improve CRM hygiene and ensure effective customer engagement tracking.

Instructions:
Please generate a report for all In Progress engagements where engagement notes are not present or are empty.

For each record, organize the output by Feature Name, and include the following details:

For Each Feature:
- Feature Name
- For each customer under that feature:
  - Customer Name (hyperlinked with respective CRM Program Participant link)
  - CxE Lead (Name + Alias)
  - Program Name
  - Engagement Status (In Progress)
  - Last Activity Date (if available)
  - Notes Status: Missing / Not Updated

Top-Level Summary (Required):
At the top of the output, include:
- Total number of In-Progress engagements
- Number of engagements missing notes
- Number of unique features impacted
- Percentage of In-Progress engagements with missing notes

Purpose / Outcome:
This report will be used to:
- Track CRM hygiene gaps
- Improve customer engagement quality
- Drive follow-ups with CxE Leads and Program owners
- Increase visibility into feature-level customer activity

Output Format:
- Start with the Executive Summary
- Follow with a Feature-by-Feature breakdown
- Use tables or bullet points
- Keep the tone concise, factual, and action-oriented

Optional Enhancements (if data is available):
- Flag engagements missing notes for >14 days / >30 days
- Add a "Next Action Required" column
- Highlight features with multiple customers missing notes`
    },
    {
        id: "deep-engagement",
        icon: "AnalyticsView",
        title: "Deep Engagement Analysis",
        description: "Analyze customer engagement from notes, feedback, gaps, and feature requests",
        prompt: "From the notes data: Which customers are deeply engaged for which features in the past 1 week? List scenarios, feedback, and gaps. What features are they requesting along with feature evidences?"
    },
    {
        id: "status-summary",
        icon: "BarChartVertical",
        title: "Status Summary",
        description: "Get a breakdown of customers by their current status",
        prompt: "Give me a summary of customers by status. How many are Nominated, Pending, Committed, In Progress, Completed, and Declined?"
    },
    {
        id: "squad-overview",
        icon: "Org",
        title: "Squad Overview",
        description: "Overview of squads and their customer engagements",
        prompt: "Give me an overview of all squads. Which squads have the most customers? Which squads have customers in active testing (In Progress)?"
    }
];

// Inline styles
const PANEL_STYLES = {
    container: {
        display: "flex",
        flexDirection: "column" as const,
        height: "100%",
        background: "#f5f5f5"
    },
    header: {
        padding: "16px 20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: "12px"
    },
    headerIcon: {
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px"
    },
    headerTitle: {
        fontSize: "18px",
        fontWeight: 600
    },
    headerSubtitle: {
        fontSize: "12px",
        opacity: 0.9
    },
    scenariosContainer: {
        padding: "16px",
        background: "#fff",
        borderBottom: "1px solid #e1e1e1"
    },
    scenariosTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#333",
        marginBottom: "12px",
        display: "flex",
        alignItems: "center",
        gap: "6px"
    },
    scenariosGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "10px"
    },
    scenarioCard: {
        padding: "12px",
        background: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },
    scenarioCardHover: {
        background: "#e8f4fd",
        borderColor: "#667eea",
        boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)"
    },
    scenarioIcon: {
        width: "28px",
        height: "28px",
        borderRadius: "6px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "14px",
        marginBottom: "8px"
    },
    scenarioTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#1a1a2e",
        marginBottom: "4px"
    },
    scenarioDesc: {
        fontSize: "11px",
        color: "#64748b",
        lineHeight: 1.4
    },
    chatContainer: {
        flex: 1,
        overflowY: "auto" as const,
        padding: "16px",
        display: "flex",
        flexDirection: "column" as const,
        gap: "12px"
    },
    messageUser: {
        alignSelf: "flex-end" as const,
        maxWidth: "80%",
        padding: "12px 16px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        borderRadius: "16px 16px 4px 16px",
        fontSize: "14px",
        lineHeight: 1.5
    },
    messageAssistant: {
        alignSelf: "flex-start" as const,
        maxWidth: "85%",
        padding: "14px 18px",
        background: "#fff",
        borderRadius: "16px 16px 16px 4px",
        fontSize: "14px",
        lineHeight: 1.6,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e2e8f0"
    },
    // Markdown content styles
    markdownContent: {
        "& h1, & h2, & h3, & h4": {
            marginTop: "16px",
            marginBottom: "8px",
            fontWeight: 600,
            color: "#1a1a2e"
        },
        "& h3": {
            fontSize: "16px",
            color: "#4338ca",
            borderBottom: "2px solid #e0e7ff",
            paddingBottom: "6px"
        },
        "& ol, & ul": {
            paddingLeft: "20px",
            margin: "8px 0"
        },
        "& li": {
            marginBottom: "8px",
            lineHeight: 1.6
        },
        "& strong": {
            fontWeight: 600,
            color: "#1e40af"
        },
        "& p": {
            margin: "8px 0"
        }
    },
    messageLoading: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color: "#64748b"
    },
    suggestions: {
        display: "flex",
        flexWrap: "wrap" as const,
        gap: "8px",
        marginTop: "12px"
    },
    suggestionChip: {
        padding: "6px 12px",
        background: "#e0e7ff",
        color: "#3730a3",
        borderRadius: "16px",
        fontSize: "12px",
        cursor: "pointer",
        border: "none",
        transition: "all 0.2s ease"
    },
    dataReferences: {
        marginTop: "12px",
        padding: "12px",
        background: "#f8fafc",
        borderRadius: "8px",
        border: "1px solid #e2e8f0"
    },
    dataRefTitle: {
        fontSize: "11px",
        fontWeight: 600,
        color: "#64748b",
        textTransform: "uppercase" as const,
        letterSpacing: "0.5px",
        marginBottom: "8px"
    },
    dataRefItem: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        background: "#e0f2fe",
        color: "#0369a1",
        borderRadius: "4px",
        fontSize: "12px",
        margin: "2px"
    },
    inputContainer: {
        padding: "16px",
        background: "#fff",
        borderTop: "1px solid #e1e1e1",
        display: "flex",
        gap: "12px",
        alignItems: "flex-end"
    },
    emptyState: {
        flex: 1,
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        textAlign: "center" as const,
        color: "#64748b"
    },
    emptyIcon: {
        fontSize: "48px",
        marginBottom: "16px",
        opacity: 0.5
    },
    dataSummary: {
        padding: "12px 16px",
        background: "#e8f5e9",
        borderBottom: "1px solid #c8e6c9",
        fontSize: "12px",
        color: "#2e7d32",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    }
};

/**
 * Generate data context summary from customers data
 */
const generateDataContext = (customers: IBwCCustomer[]) => {
    if (!customers || customers.length === 0) {
        return {
            totalCustomers: 0,
            totalEngagements: 0,
            totalPrograms: 0,
            statusSummary: {} as { [status: string]: number },
            uniqueCustomersByStatus: "{}",
            recentActivity: "No data available",
            customersData: "[]"
        };
    }

    // Count unique customers and programs
    const uniqueCustomers = new Set(customers.map(c => c.tenantId || c.customerName));
    const uniquePrograms = new Set(customers.map(c => c.programId));
    const uniqueSquads = new Set(customers.map(c => c.squadDescription).filter(Boolean));

    // Status summary - counts ENGAGEMENTS (records) per status
    const statusSummary: { [status: string]: number } = {};
    customers.forEach(c => {
        const status = c.customerStatus || "Unknown";
        statusSummary[status] = (statusSummary[status] || 0) + 1;
    });

    // Unique customers by status - counts UNIQUE CUSTOMERS (by tenantId) per status
    const uniqueCustomerIdsByStatus: { [status: string]: Set<string> } = {};
    customers.forEach(c => {
        const status = c.customerStatus || "Unknown";
        const customerId = c.tenantId || c.customerName;
        if (customerId) {
            if (!uniqueCustomerIdsByStatus[status]) {
                uniqueCustomerIdsByStatus[status] = new Set();
            }
            uniqueCustomerIdsByStatus[status].add(customerId);
        }
    });
    // Convert Sets to counts
    const uniqueCustomersByStatus: { [status: string]: number } = {};
    Object.entries(uniqueCustomerIdsByStatus).forEach(([status, ids]) => {
        uniqueCustomersByStatus[status] = ids.size;
    });

    // Region summary
    const regionSummary: { [region: string]: number } = {};
    customers.forEach(c => {
        const region = c.customerRegion || "Unknown";
        regionSummary[region] = (regionSummary[region] || 0) + 1;
    });

    // Squad/Program summary with customer counts
    const squadSummary: { [squad: string]: { customerCount: number; inProgressCount: number } } = {};
    customers.forEach(c => {
        const squad = c.programName || "Unknown";
        if (!squadSummary[squad]) {
            squadSummary[squad] = { customerCount: 0, inProgressCount: 0 };
        }
        squadSummary[squad].customerCount++;
        if (c.customerStatus === "In Progress") {
            squadSummary[squad].inProgressCount++;
        }
    });

    // Recent activity (last 7 days)
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const recentCustomers = customers.filter(c => {
        if (!c.createdOn) return false;
        return new Date(c.createdOn) >= oneWeekAgo;
    });

    // Get details of recently added customers
    const recentCustomerDetails = recentCustomers.map(c => ({
        customerName: c.customerName,
        programName: c.programName,
        status: c.customerStatus,
        region: c.customerRegion,
        cxELead: c.cxELead,
        assignedPM: c.assignedPM,
        createdOn: c.createdOn
    }));

    // Customers with recent notes activity (last 14 days)
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const customersWithRecentNotes = customers.filter(c => {
        if (!c.lastModifiedNotesDate) return false;
        return new Date(c.lastModifiedNotesDate) >= twoWeeksAgo;
    });

    // Customers missing notes (In Progress with no notes or empty notes)
    const customersWithMissingNotes = customers.filter(c =>
        c.customerStatus === "In Progress" && (!c.notes || c.notes.trim() === "" || /^[-—]+$/.test(c.notes))
    );

    // Feature evidence summary
    const totalFeatureEvidence = customers.reduce((sum, c) => sum + (c.featureEvidenceCount || 0), 0);
    const customersWithFeatures = customers.filter(c => (c.featureEvidenceCount || 0) > 0);

    // Prepare comprehensive customer data (limit to avoid token overflow)
    const summaryData = customers.slice(0, 100).map(c => ({
        customerName: c.customerName,
        tenantId: c.tenantId,
        status: c.customerStatus,
        previousStatus: c.previousStatus,
        programName: c.programName,
        programProduct: c.programProduct,
        squadDescription: c.squadDescription,
        region: c.customerRegion,
        squadLeader: c.squadLeader,
        groupSquadLeader: c.groupSquadLeader,
        cxELead: c.cxELead,
        assignedPM: c.assignedPM,
        assignedPMAlias: c.assignedPMAlias,
        contributingPMs: c.contributingPMs,
        participantStage: c.participantStage,
        engagementNotes: c.engagementNotes?.substring(0, 300) || "",
        notes: c.notes?.substring(0, 300) || "",
        lastModifiedNotesDate: c.lastModifiedNotesDate,
        featureEvidenceCount: c.featureEvidenceCount || 0,
        createdOn: c.createdOn,
        participantCreatedOn: c.participantCreatedOn,
        programParticipantURL: c.programParticipantURL
    }));

    // Customers missing notes details for hygiene report
    const missingNotesDetails = customersWithMissingNotes.slice(0, 50).map(c => ({
        customerName: c.customerName,
        programName: c.programName,
        cxELead: c.cxELead,
        assignedPM: c.assignedPM,
        lastModifiedNotesDate: c.lastModifiedNotesDate,
        programParticipantURL: c.programParticipantURL
    }));

    return {
        totalCustomers: uniqueCustomers.size,
        totalEngagements: customers.length,
        totalPrograms: uniquePrograms.size,
        totalSquads: uniqueSquads.size,
        statusSummary,
        uniqueCustomersByStatus: JSON.stringify(uniqueCustomersByStatus),
        regionSummary: JSON.stringify(regionSummary),
        squadSummary: JSON.stringify(squadSummary),
        totalFeatureEvidence,
        customersWithFeatures: customersWithFeatures.length,
        recentActivity: `${recentCustomers.length} new engagements in the past week`,
        recentCustomers: JSON.stringify(recentCustomerDetails),
        customersWithRecentNotes: customersWithRecentNotes.length,
        missingNotesCount: customersWithMissingNotes.length,
        missingNotesDetails: JSON.stringify(missingNotesDetails),
        customersData: JSON.stringify(summaryData)
    };
};

// Inline styles for markdown elements (applied via className)
const markdownStyles = `
    .bwc-ai-markdown h1,
    .bwc-ai-markdown h2,
    .bwc-ai-markdown h3,
    .bwc-ai-markdown h4 {
        margin-top: 16px;
        margin-bottom: 8px;
        font-weight: 600;
        color: #1a1a2e;
    }
    .bwc-ai-markdown h3 {
        font-size: 15px;
        color: #4338ca;
        border-bottom: 2px solid #e0e7ff;
        padding-bottom: 6px;
        margin-top: 20px;
    }
    .bwc-ai-markdown ol,
    .bwc-ai-markdown ul {
        padding-left: 20px;
        margin: 8px 0;
    }
    .bwc-ai-markdown li {
        margin-bottom: 10px;
        line-height: 1.6;
        padding-left: 4px;
    }
    .bwc-ai-markdown strong {
        font-weight: 600;
        color: #1e40af;
        background: linear-gradient(120deg, #e0e7ff 0%, #c7d2fe 100%);
        padding: 1px 6px;
        border-radius: 4px;
    }
    .bwc-ai-markdown p {
        margin: 8px 0;
        line-height: 1.6;
    }
    .bwc-ai-markdown p:first-child {
        margin-top: 0;
    }
    .bwc-ai-markdown hr {
        border: none;
        border-top: 1px solid #e2e8f0;
        margin: 16px 0;
    }
    .bwc-ai-markdown code {
        background: #f1f5f9;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: 'Consolas', monospace;
        font-size: 13px;
    }
`;

/**
 * Chat Message Component
 */
const ChatMessage: React.FC<{ message: IBwCAIChatMessage; onSuggestionClick?: (prompt: string) => void }> = ({
    message,
    onSuggestionClick
}) => {
    if (message.role === "user") {
        return <div style={PANEL_STYLES.messageUser}>{message.content}</div>;
    }

    return (
        <div style={PANEL_STYLES.messageAssistant}>
            <style>{markdownStyles}</style>
            {message.isLoading ? (
                <div style={PANEL_STYLES.messageLoading}>
                    <Spinner size={SpinnerSize.small} />
                    <span>Analyzing data...</span>
                </div>
            ) : (
                <>
                    <div className="bwc-ai-markdown">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>

                    {/* Data References */}
                    {message.dataReferences && (
                        <div style={PANEL_STYLES.dataReferences}>
                            {message.dataReferences.customerNames && message.dataReferences.customerNames.length > 0 && (
                                <div style={{ marginBottom: "8px" }}>
                                    <div style={PANEL_STYLES.dataRefTitle}>Referenced Customers</div>
                                    {message.dataReferences.customerNames.map((name, i) => (
                                        <span key={i} style={PANEL_STYLES.dataRefItem}>
                                            <Icon iconName="Contact" style={{ fontSize: 10 }} />
                                            {name}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {message.dataReferences.metrics && message.dataReferences.metrics.length > 0 && (
                                <div>
                                    <div style={PANEL_STYLES.dataRefTitle}>Key Metrics</div>
                                    {message.dataReferences.metrics.map((metric, i) => (
                                        <span key={i} style={{ ...PANEL_STYLES.dataRefItem, background: "#fef3c7", color: "#92400e" }}>
                                            {metric.label}: <strong>{metric.value}</strong>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Suggestions */}
                    {message.suggestions && message.suggestions.length > 0 && (
                        <div style={PANEL_STYLES.suggestions}>
                            {message.suggestions.map((suggestion, i) => (
                                <button
                                    key={i}
                                    style={PANEL_STYLES.suggestionChip}
                                    onClick={() => onSuggestionClick?.(suggestion)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "#c7d2fe";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "#e0e7ff";
                                    }}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * Scenario Card Component
 */
const ScenarioCard: React.FC<{
    scenario: IBwCAIPromptScenario;
    onClick: () => void;
}> = ({ scenario, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            style={{
                ...PANEL_STYLES.scenarioCard,
                ...(isHovered ? PANEL_STYLES.scenarioCardHover : {})
            }}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div style={PANEL_STYLES.scenarioIcon}>
                <Icon iconName={scenario.icon} />
            </div>
            <div style={PANEL_STYLES.scenarioTitle}>{scenario.title}</div>
            <div style={PANEL_STYLES.scenarioDesc}>{scenario.description}</div>
        </div>
    );
};

/**
 * Main BwC AI Assistant Panel Component
 */
export const BwCAIAssistantPanel: React.FC<IBwCAIAssistantPanelProps> = ({
    isOpen,
    onDismiss,
    customers,
    isLoadingData,
    initialPrompt,
    onInitialPromptSent,
    messages,
    onMessagesChange,
    sessionId,
    onSessionIdChange
}) => {
    const [inputValue, setInputValue] = useState("");
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    // Track which prompt was last processed to avoid duplicates
    const lastProcessedPromptRef = useRef<string | undefined>(undefined);

    // Use ref to track if component is mounted (for handling responses after panel close)
    const isMountedRef = useRef(true);
    const messagesRef = useRef(messages);

    // Keep messagesRef in sync with messages prop
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    // Track mounted state
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // RTK Query mutation for AI chat
    const [askAI, { isLoading: isAILoading }] = useAskBwCAIAssistantMutation();

    // Generate data context from customers
    const dataContext = useMemo(() => generateDataContext(customers), [customers]);

    // Helper function to scroll chat to bottom with multiple attempts
    // eslint-disable-next-line no-undef
    const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth", attempts = 1) => {
        const doScroll = () => {
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTo({
                    top: chatContainerRef.current.scrollHeight,
                    behavior
                });
            }
        };

        // For multiple attempts, use increasing delays to account for panel animation
        if (attempts > 1) {
            const delays = [0, 100, 300, 500, 700];
            delays.forEach(delay => {
                setTimeout(() => {
                    requestAnimationFrame(doScroll);
                }, delay);
            });
        } else {
            requestAnimationFrame(doScroll);
        }
    }, []);

    // Track previous loading state to detect when loading starts
    const prevLoadingRef = useRef(false);

    // Scroll input into view when panel opens
    useEffect(() => {
        if (isOpen) {
            // Multiple attempts to ensure input is visible after panel animation
            const delays = [100, 300, 500];
            delays.forEach(delay => {
                setTimeout(() => {
                    inputContainerRef.current?.scrollIntoView({ behavior: "instant", block: "end" });
                }, delay);
            });
        }
    }, [isOpen]);

    // Scroll when loading starts (new message being processed)
    useEffect(() => {
        if (isAILoading && !prevLoadingRef.current) {
            // Loading just started - scroll to show loading indicator
            scrollToBottom("instant", 5);
        }
        prevLoadingRef.current = isAILoading;
    }, [isAILoading, scrollToBottom]);

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        if (messages.length > 0) {
            scrollToBottom("smooth");
        }
    }, [messages, scrollToBottom]);

    /**
     * Send message to AI
     * Uses refs to handle the case where panel is closed while request is in-flight
     */
    const sendMessage = useCallback(async (prompt: string) => {
        if (!prompt.trim() || isAILoading) return;

        const userMessage: IBwCAIChatMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            content: prompt.trim(),
            timestamp: new Date()
        };

        const loadingMessageId = `assistant-${Date.now()}`;
        const loadingMessage: IBwCAIChatMessage = {
            id: loadingMessageId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            isLoading: true
        };

        // Update messages through parent state
        onMessagesChange([...messagesRef.current, userMessage, loadingMessage]);
        setInputValue("");

        try {
            const response = await askAI({
                question: prompt.trim(),
                sessionId,
                conversationHistory: messagesRef.current.filter(m => !m.isLoading)
            }).unwrap();

            // Replace loading message with actual response
            // Use messagesRef to get current state even if panel was closed
            const assistantMessage: IBwCAIChatMessage = {
                id: `assistant-${Date.now()}`,
                role: "assistant",
                content: response.answer,
                timestamp: new Date(),
                suggestions: response.suggestions,
                dataReferences: response.dataReferences
            };

            onMessagesChange(messagesRef.current.map(m => (m.id === loadingMessageId ? assistantMessage : m)));

            if (response.sessionId) {
                onSessionIdChange(response.sessionId);
            }
        } catch {
            // Handle error - show error message
            const errorMessage: IBwCAIChatMessage = {
                id: `assistant-${Date.now()}`,
                role: "assistant",
                content: "Sorry, I encountered an error while processing your request. Please try again.",
                timestamp: new Date()
            };
            onMessagesChange(messagesRef.current.map(m => (m.id === loadingMessageId ? errorMessage : m)));
        }
    }, [askAI, isAILoading, onMessagesChange, onSessionIdChange, sessionId]);

    // Auto-send initial prompt when panel opens with one
    // Uses ref to track which prompt was processed to avoid duplicates
    useEffect(() => {
        if (isOpen && initialPrompt && lastProcessedPromptRef.current !== initialPrompt && !isAILoading) {
            lastProcessedPromptRef.current = initialPrompt;
            sendMessage(initialPrompt);
            onInitialPromptSent?.();
        }
    }, [isOpen, initialPrompt, isAILoading, sendMessage, onInitialPromptSent]);

    /**
     * Handle scenario card click
     */
    const handleScenarioClick = (scenario: IBwCAIPromptScenario) => {
        sendMessage(scenario.prompt);
    };

    /**
     * Handle input submit
     */
    const handleSubmit = () => {
        if (inputValue.trim()) {
            sendMessage(inputValue);
        }
    };

    /**
     * Handle key press (Enter to submit)
     */
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    /**
     * Clear conversation and start fresh
     */
    const handleClearConversation = () => {
        onMessagesChange([]);
        onSessionIdChange(`bwc-ai-${Date.now()}`);
        lastProcessedPromptRef.current = undefined;
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText=""
            styles={{
                main: { maxWidth: "600px" },
                content: { padding: 0 },
                scrollableContent: { height: "100%" },
                commands: { display: "none" }
            }}
            isLightDismiss
        >
            <div style={PANEL_STYLES.container}>
                {/* Header */}
                <div style={PANEL_STYLES.header}>
                    <div style={PANEL_STYLES.headerIcon}>
                        <Icon iconName="Robot" />
                    </div>
                    <div>
                        <div style={PANEL_STYLES.headerTitle}>BwC AI Assistant</div>
                        <div style={PANEL_STYLES.headerSubtitle}>
                            Ask questions about your Build with Customer data
                        </div>
                    </div>
                    <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                        {messages.length > 0 && (
                            <TooltipHost content="Clear conversation">
                                <IconButton
                                    iconProps={{ iconName: "Delete" }}
                                    onClick={handleClearConversation}
                                    disabled={isAILoading}
                                    styles={{
                                        root: { color: "#fff" },
                                        rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" },
                                        rootDisabled: { color: "rgba(255,255,255,0.5)" }
                                    }}
                                />
                            </TooltipHost>
                        )}
                        <IconButton
                            iconProps={{ iconName: "Cancel" }}
                            onClick={onDismiss}
                            styles={{
                                root: { color: "#fff" },
                                rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" }
                            }}
                        />
                    </div>
                </div>

                {/* Data Summary Bar */}
                {!isLoadingData && customers.length > 0 && (
                    <div style={PANEL_STYLES.dataSummary}>
                        <Icon iconName="Database" />
                        <span>
                            Loaded {dataContext.totalCustomers} unique customers, {dataContext.totalEngagements} engagements across {dataContext.totalPrograms} programs
                        </span>
                    </div>
                )}

                {/* Prompt Scenarios (show when no messages) */}
                {messages.length === 0 && (
                    <div style={PANEL_STYLES.scenariosContainer}>
                        <div style={PANEL_STYLES.scenariosTitle}>
                            <Icon iconName="Lightbulb" style={{ color: "#667eea" }} />
                            Quick Insights - Click to explore
                        </div>
                        <div style={PANEL_STYLES.scenariosGrid}>
                            {AI_PROMPT_SCENARIOS.map(scenario => (
                                <ScenarioCard
                                    key={scenario.id}
                                    scenario={scenario}
                                    onClick={() => handleScenarioClick(scenario)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Chat Messages */}
                <div ref={chatContainerRef} style={PANEL_STYLES.chatContainer}>
                    {isLoadingData ? (
                        <div style={PANEL_STYLES.emptyState}>
                            <Spinner size={SpinnerSize.large} />
                            <div style={{ marginTop: "12px" }}>Loading customer data...</div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div style={PANEL_STYLES.emptyState}>
                            <Icon iconName="ChatBot" style={PANEL_STYLES.emptyIcon} />
                            <div style={{ fontSize: "16px", fontWeight: 500, marginBottom: "8px" }}>
                                Start a conversation
                            </div>
                            <div style={{ fontSize: "13px" }}>
                                Choose a quick insight above or type your own question below
                            </div>
                        </div>
                    ) : (
                        messages.map(message => (
                            <ChatMessage
                                key={message.id}
                                message={message}
                                onSuggestionClick={sendMessage}
                            />
                        ))
                    )}
                </div>

                {/* Input Area */}
                <div ref={inputContainerRef} style={PANEL_STYLES.inputContainer}>
                    <TextField
                        placeholder="Ask a question about BwC data..."
                        value={inputValue}
                        onChange={(_, newValue) => setInputValue(newValue || "")}
                        onKeyPress={handleKeyPress}
                        multiline
                        autoAdjustHeight
                        disabled={isLoadingData || isAILoading}
                        styles={{
                            root: { flex: 1 },
                            fieldGroup: {
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0"
                            }
                        }}
                    />
                    <TooltipHost content="Send message" directionalHint={DirectionalHint.topCenter}>
                        <IconButton
                            iconProps={{ iconName: "Send" }}
                            onClick={handleSubmit}
                            disabled={!inputValue.trim() || isLoadingData || isAILoading}
                            styles={{
                                root: {
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                    background: inputValue.trim() ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "#e2e8f0",
                                    color: inputValue.trim() ? "#fff" : "#94a3b8"
                                },
                                rootHovered: {
                                    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                                    color: "#fff"
                                },
                                rootDisabled: {
                                    background: "#e2e8f0",
                                    color: "#94a3b8"
                                }
                            }}
                        />
                    </TooltipHost>
                </div>
            </div>
        </Panel>
    );
};

export default BwCAIAssistantPanel;
